import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import { PUBLIC_API_BASE_PATH } from 'src/constants/environment';
import type { ReviewsPageResponse } from './types';

export const REVIEWS_API_PATH = `${PUBLIC_API_BASE_PATH}reviews`;

/**
 * Longer than the route's own 3s budget against Feefo, so an answer just inside that limit
 * is not cut off here.
 */
const CLIENT_TIMEOUT_MS = 6000;

/**
 * Weighted to the tail rather than tight. Feefo finishes computing a cold query even when
 * the request is aborted and caches the result, so the first attempt mostly starts the
 * clock; cold queries on a 2,102-review product took ~4s to ~50s against ~0.08s warm.
 * With each failed attempt costing ~3s, the last one begins near 56s, past the slowest
 * cold query observed.
 */
export const RETRY_DELAYS_MS = [2000, 5000, 12000, 25000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface Attempt {
  payload?: ReviewsPageResponse;
  /** Whether asking again could plausibly give a different answer. */
  retryable: boolean;
}

const attemptFetch = async (url: string): Promise<Attempt> => {
  try {
    const response = await fetchJSONWithTimeout(url, { timeout: CLIENT_TIMEOUT_MS });

    if (response.ok) {
      return { payload: await response.json(), retryable: false };
    }

    // Only a 5xx, which the route returns when its own Feefo call fails or times out, is worth retrying.
    return { retryable: response.status >= 500 };
  } catch {
    // A timeout or network failure: the cold-Feefo case the delays above exist for.
    return { retryable: true };
  }
};

export interface FetchProductReviewsClientOptions {
  page?: number;
}

/**
 * A page of reviews from our own API route, retried through a cold upstream. Resolves
 * null once the retries are exhausted. An OK response with no reviews is returned as-is:
 * a product with nothing to show is an answer, not a failure.
 */
export const fetchProductReviewsClient = async (
  productCode: number,
  { page }: FetchProductReviewsClientOptions = {},
): Promise<ReviewsPageResponse | null> => {
  const params = new URLSearchParams();

  if (page) {
    params.set('page', String(page));
  }

  const query = params.toString();
  const url = `${REVIEWS_API_PATH}/${productCode}${query ? `?${query}` : ''}`;

  for (let attempt = 0; ; attempt += 1) {
    const { payload, retryable } = await attemptFetch(url);

    if (payload) {
      return payload;
    }

    if (!retryable || attempt >= RETRY_DELAYS_MS.length) {
      return null;
    }

    await wait(RETRY_DELAYS_MS[attempt]);
  }
};
